/**
 * BirthdayPremium — Premium, story-driven birthday invitation template.
 *
 * Brand-new template (id `birthday-premium`) — does NOT replace or modify the
 * frozen `birthday-celebration` template. Deep blue theme with floating
 * balloons, full-screen hero (with optional background blur), and animated
 * sections for timeline, gallery, celebrants, schedule, venue, countdown,
 * fun facts, plus the interactive Wishes Wall and Memory Capsule (live guest
 * submissions backed by the API).
 *
 * Sections render conditionally — empty data = section hidden.
 *
 * customData.birthday shape → see BirthdayCustomData in src/types.ts
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { bdayTheme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { SnapshotGallerySection } from './sections/SnapshotGallerySection';
import { CelebrantsSection } from './sections/CelebrantsSection';
import { HappyMembersSection } from './sections/HappyMembersSection';
import { ScheduleSection } from './sections/ScheduleSection';
import { VenueSection } from './sections/VenueSection';
import { RsvpSection } from './sections/RsvpSection';
import { MemoryTimelineSection } from './sections/MemoryTimelineSection';
import { CountdownSection } from './sections/CountdownSection';
import { FunFactsSection } from './sections/FunFactsSection';
import { WishesWallSection } from './sections/WishesWallSection';
import { MemoryCapsuleSection } from './sections/MemoryCapsuleSection';
import { BirthdayStorySection } from './sections/BirthdayStorySection';

const META: TemplateMetadata = {
  id: 'birthday-premium',
  name: 'Birthday Premium',
  occasionType: 'BIRTHDAY',
  description:
    'Premium story-driven birthday invitation: full-screen hero, memory timeline, snapshot gallery, celebrants, activity schedule, countdown, fun facts, interactive wishes wall, and memory capsule.',
  isPremium: true,
  tags: ['birthday', 'blue', 'animated', 'multi-section', 'celebration', 'premium', 'story', 'interactive'],
  colorScheme: {
    primary: bdayTheme.accent,
    accent: bdayTheme.accentSoft,
    bg: bdayTheme.bgDeep,
  },
};

export function BirthdayPremium({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);

  const hasNewCelebrants = content.celebrants.length > 0;
  const hasNewActivities = content.activities.length > 0;

  // The interactive sections (Wishes Wall, Memory Capsule, RSVP) need the
  // invitation identity to read/write guest submissions through the API.
  // `isLive` gates real network calls — only a PUBLISHED/PROCESSING invitation
  // accepts guest submissions; drafts and the wizard preview stay local-only.
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return (
    <main
      className="min-h-screen scroll-smooth font-sans antialiased"
      style={{
        ...themeCssVars(bdayTheme),
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      {/* 1 — Hero */}
      <HeroSection content={content.hero} />

      {/* 2 — Countdown (auto-hides when event has passed) */}
      <CountdownSection content={content} />

      {/* 3 — Birthday Story (user-authored; gated by showBirthdayStory flag) */}
      {content.showBirthdayStory && <BirthdayStorySection content={content} />}

      {/* 4 — Memory Timeline (gated by showMemoryTimeline flag + requires items) */}
      {content.showMemoryTimeline && <MemoryTimelineSection content={content} />}

      {/* 5 — Snapshot Gallery (photos with captions) */}
      <SnapshotGallerySection content={content} />

      {/* 6 — Celebrants (rich grid) or legacy Happy Members */}
      {hasNewCelebrants
        ? <CelebrantsSection content={content} />
        : <HappyMembersSection content={content.happyMembers} />
      }

      {/* 7 — Activity Schedule (rich) or legacy Schedule */}
      {hasNewActivities
        ? (
          <ScheduleSection
            content={{
              label: 'The Plan',
              title: 'Party Schedule',
              items: content.activities.map((a) => ({
                time: a.time,
                activity: a.title,
                description: a.description,
                emoji: a.emoji,
              })),
            }}
            bg={content.sectionBackgrounds.schedule}
          />
        )
        : <ScheduleSection content={content.schedule} bg={content.sectionBackgrounds.schedule} />
      }

      {/* 8 — Fun Facts (gated by showFunFacts flag) */}
      {content.showFunFacts && <FunFactsSection content={content} />}

      {/* 9 — Venue */}
      <VenueSection content={content} />

      {/* 10 — RSVP CTA (live submissions when published) */}
      <RsvpSection rsvp={content.rsvp} invitationId={invitationId} isLive={isLive} />

      {/* 11 — Birthday Wishes Wall (live guest submissions) */}
      <WishesWallSection content={content} invitationId={invitationId} isLive={isLive} />

      {/* 12 — Memory Capsule (gated by showMemoryCapsule flag; live guest submissions) */}
      {content.showMemoryCapsule && (
        <MemoryCapsuleSection content={content} invitationId={invitationId} isLive={isLive} />
      )}
    </main>
  );
}

registry.register({ metadata: META, component: BirthdayPremium });
